/**
 * Server-confirmed progress helpers.
 * Readiness / timers must never mark execution steps complete.
 */

import { STEP_STATUS, OUTCOME, isConfirmedStepSuccess } from "./types";

/** Preflight / readiness states — not execution progress. */
export const READINESS_STATUS = Object.freeze({
  CHECKING: "checking",
  SATISFIED: "satisfied",
  NEEDS_FIX: "needs_fix",
  NOT_RELEVANT: "not_relevant",
  UNRESOLVED: "unresolved",
});

/**
 * True if a value looks like readiness (can_be_completed style), not execution.
 * @param {unknown} value
 */
export function isReadinessSignal(value) {
  if (value == null) return false;
  if (typeof value === "boolean") return true; // can_be_completed
  if (typeof value === "string") {
    return Object.values(READINESS_STATUS).includes(value);
  }
  if (typeof value === "object") {
    return (
      Object.prototype.hasOwnProperty.call(value, "can_be_completed") ||
      Object.prototype.hasOwnProperty.call(value, "ready")
    );
  }
  return false;
}

/**
 * Reject using readiness or elapsed time as proof of step completion.
 * @param {object} evidence
 * @param {string} [evidence.status] proposed execution status
 * @param {boolean} [evidence.fromTimer]
 * @param {boolean} [evidence.fromReadiness]
 * @param {boolean} [evidence.fromDispatchFulfilledOnly]
 * @param {boolean} [evidence.fromServerResult]
 */
export function canConfirmStepFromEvidence(evidence = {}) {
  if (evidence.fromTimer) return false;
  if (evidence.fromReadiness) return false;
  if (evidence.fromDispatchFulfilledOnly) return false;
  if (isReadinessSignal(evidence.readiness)) return false;
  if (!evidence.fromServerResult && !evidence.fromConfirmedApiResult) {
    return false;
  }
  const status = evidence.status;
  if (
    status === STEP_STATUS.COMPLETED ||
    status === STEP_STATUS.ALREADY_APPLIED ||
    status === STEP_STATUS.FAILED ||
    status === STEP_STATUS.ROLLED_BACK ||
    status === STEP_STATUS.NOT_RELEVANT ||
    status === STEP_STATUS.NOT_DONE ||
    status === STEP_STATUS.UNCONFIRMED ||
    status === STEP_STATUS.CANCELLED ||
    status === STEP_STATUS.NEEDS_FIX ||
    status === STEP_STATUS.RUNNING ||
    status === STEP_STATUS.PENDING
  ) {
    return true;
  }
  return false;
}

/**
 * Map a confirmed server step payload into STEP_STATUS.
 * Does not accept readiness booleans.
 * @param {object} payload
 */
export function mapServerStepStatus(payload = {}) {
  if (payload.already_applied === true || payload.alreadyApplied === true) {
    return STEP_STATUS.ALREADY_APPLIED;
  }
  if (
    payload.not_relevant === true ||
    payload.notRelevant === true ||
    payload.status === "not_relevant"
  ) {
    return STEP_STATUS.NOT_RELEVANT;
  }
  if (payload.rolled_back === true || payload.status === "rolled_back") {
    return STEP_STATUS.ROLLED_BACK;
  }
  if (payload.cancelled === true || payload.status === "cancelled") {
    return STEP_STATUS.CANCELLED;
  }
  if (payload.failed === true || payload.status === "failed") {
    return STEP_STATUS.FAILED;
  }
  if (
    payload.completed === true ||
    payload.status === "completed" ||
    payload.ok === true
  ) {
    return STEP_STATUS.COMPLETED;
  }
  if (payload.status === "running" || payload.status === "pending") {
    return payload.status;
  }
  if (payload.status === "unconfirmed" || payload.unconfirmed === true) {
    return STEP_STATUS.UNCONFIRMED;
  }
  // Explicit server status string if already a known STEP_STATUS
  if (
    typeof payload.status === "string" &&
    Object.values(STEP_STATUS).includes(payload.status)
  ) {
    return payload.status;
  }
  return STEP_STATUS.UNCONFIRMED;
}

/**
 * Aggregate selected/promised execution steps into an operation outcome.
 * Skipped / not_relevant steps are not counted as success.
 * @param {Array<{ status: string, promised?: boolean }>} steps
 */
export function aggregateOperationOutcome(steps = []) {
  const relevant = steps.filter(
    (s) =>
      s &&
      s.status !== STEP_STATUS.NOT_RELEVANT &&
      s.status !== STEP_STATUS.NOT_DONE &&
      s.promised !== false
  );
  if (relevant.length === 0) {
    return { outcome: OUTCOME.APPLIED, note: "no_promised_steps" };
  }

  const hasFailed = relevant.some(
    (s) =>
      s.status === STEP_STATUS.FAILED || s.status === STEP_STATUS.ROLLED_BACK
  );
  const hasUnconfirmed = relevant.some(
    (s) =>
      s.status === STEP_STATUS.UNCONFIRMED ||
      s.status === STEP_STATUS.RUNNING ||
      s.status === STEP_STATUS.PENDING ||
      s.status === STEP_STATUS.CANCELLED
  );
  const allSuccess = relevant.every((s) => isConfirmedStepSuccess(s.status));

  if (allSuccess) {
    return { outcome: OUTCOME.APPLIED, note: "all_confirmed" };
  }
  if (hasFailed && relevant.some((s) => isConfirmedStepSuccess(s.status))) {
    return { outcome: OUTCOME.PARTIAL, note: "mixed_success_and_failure" };
  }
  if (hasFailed) {
    return { outcome: OUTCOME.NOT_APPLIED, note: "failed" };
  }
  if (hasUnconfirmed) {
    return { outcome: OUTCOME.UNKNOWN, note: "unconfirmed" };
  }
  return { outcome: OUTCOME.UNKNOWN, note: "mixed" };
}

/**
 * After a confirmed save, a failed refresh must not rewrite save to failed.
 * @param {string} confirmedSaveStatus
 * @param {boolean} refreshFailed
 */
export function outcomeAfterRefreshFailure(confirmedSaveStatus, refreshFailed) {
  if (!refreshFailed) {
    return {
      saveStatus: confirmedSaveStatus,
      outcome: isConfirmedStepSuccess(confirmedSaveStatus)
        ? OUTCOME.APPLIED
        : OUTCOME.UNKNOWN,
      summaryKey: null,
    };
  }
  if (isConfirmedStepSuccess(confirmedSaveStatus)) {
    return {
      saveStatus: confirmedSaveStatus,
      refreshStatus: STEP_STATUS.FAILED,
      outcome: OUTCOME.PARTIAL,
      summaryKey: "operation_feedback.saved_list_refresh_failed",
    };
  }
  return {
    saveStatus: confirmedSaveStatus,
    refreshStatus: STEP_STATUS.FAILED,
    outcome: OUTCOME.UNKNOWN,
    summaryKey: null,
  };
}
